import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AUTOMATIC_SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { VehicleClass } from '@/shared/models/ContextDataModel/interfaces/entities/vehicle-class.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { useQuantityHelpers } from './useQuantityHelpers.ts';
import { useSharedFormQuantities } from './useSharedFormQuantities.ts';
import { deriveQuantity } from '../utils/deriveQuantity.ts';

export function usePositionFormQuantities(methods: UseFormReturn<AutomaticProductsFormValues>) {
    const { getValues, setValue } = methods;
    const { recalculatePurchaseQuantities, recalculateAll, recalculateAdditionalServices } =
        useQuantityHelpers(methods);
    const shared = useSharedFormQuantities(methods);

    const onSalesMaterialQuantityChange = useCallback(
        (quantity: number | undefined) => {
            if (quantity === undefined) {
                return;
            }
            const [salesMaterialUnit, vehicleClass] = getValues(['salesMaterialUnit', 'vehicleClass']);

            if (vehicleClass && salesMaterialUnit) {
                recalculateAll(quantity, salesMaterialUnit, vehicleClass.payloadInTons);
            }
        },
        [getValues, recalculateAll]
    );

    const onVehicleClassChange = useCallback(
        (vehicleClass?: VehicleClass) => {
            if (!vehicleClass) {
                return;
            }
            const payloadInTons = vehicleClass.payloadInTons;
            const [productType, salesMaterialUnit, salesTransportUnit, salesMaterialQuantity, salesTransportQuantity] =
                getValues([
                    'productType',
                    'salesMaterialUnit',
                    'salesTransportUnit',
                    'salesMaterialQuantity',
                    'salesTransportQuantity',
                ]);

            if (isTransportProduct(productType)) {
                if (salesTransportQuantity !== undefined && salesTransportUnit) {
                    recalculatePurchaseQuantities(salesTransportQuantity, salesTransportUnit, payloadInTons);
                }
            } else {
                if (salesMaterialQuantity !== undefined && salesMaterialUnit) {
                    recalculateAll(salesMaterialQuantity, salesMaterialUnit, payloadInTons);
                }
            }
        },
        [getValues, recalculateAll, recalculatePurchaseQuantities]
    );

    const onSalesMaterialUnitChange = useCallback(
        (salesMaterialUnit?: AUTOMATIC_SUPPORTED_UNITS) => {
            if (!salesMaterialUnit) {
                return;
            }
            const [salesMaterialQuantity, vehicleClass] = getValues(['salesMaterialQuantity', 'vehicleClass']);

            if (salesMaterialQuantity !== undefined && vehicleClass) {
                recalculateAll(salesMaterialQuantity, salesMaterialUnit, vehicleClass.payloadInTons);
            }
        },
        [getValues, recalculateAll]
    );

    const onSalesTransportQuantityChange = useCallback(
        (quantity: number | undefined) => {
            if (quantity === undefined) {
                return;
            }
            const [salesTransportUnit, vehicleClass, purchaseTransportUnit] = getValues([
                'salesTransportUnit',
                'vehicleClass',
                'purchaseTransportUnit',
            ]);
            if (salesTransportUnit && purchaseTransportUnit && vehicleClass) {
                setValue(
                    'purchaseTransportQuantity',
                    deriveQuantity(quantity, salesTransportUnit, purchaseTransportUnit, vehicleClass.payloadInTons)
                );
            }
            if (salesTransportUnit && vehicleClass) {
                recalculateAdditionalServices(quantity, salesTransportUnit, vehicleClass.payloadInTons);
            }
        },
        [getValues, setValue, recalculateAdditionalServices]
    );

    const onSalesTransportUnitChange = useCallback(
        (unit?: AUTOMATIC_SUPPORTED_UNITS) => {
            if (!unit) {
                return;
            }
            const [productType, salesMaterialQuantity, salesMaterialUnit, salesTransportQuantity, vehicleClass] =
                getValues([
                    'productType',
                    'salesMaterialQuantity',
                    'salesMaterialUnit',
                    'salesTransportQuantity',
                    'vehicleClass',
                ]);
            if (!vehicleClass) {
                return;
            }

            if (isTransportProduct(productType)) {
                if (salesTransportQuantity !== undefined) {
                    recalculatePurchaseQuantities(salesTransportQuantity, unit, vehicleClass.payloadInTons);
                }
            } else {
                if (salesMaterialQuantity !== undefined && salesMaterialUnit) {
                    setValue(
                        'salesTransportQuantity',
                        deriveQuantity(salesMaterialQuantity, salesMaterialUnit, unit, vehicleClass.payloadInTons)
                    );
                }
            }
        },
        [getValues, setValue, recalculatePurchaseQuantities]
    );

    return {
        ...shared,
        onSalesMaterialUnitChange,
        onSalesMaterialQuantityChange,
        onVehicleClassChange,
        onSalesTransportQuantityChange,
        onSalesTransportUnitChange,
    };
}
